/**
 * General Ledger — /finance/reports/gl
 *
 * Per-account drill-through with date range and running balance.
 * 1. Load chart-of-accounts for the account picker.
 * 2. On account select, call GET /finance/reports/gl?accountId=...&from=...&to=...
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BookOpen, RefreshCw, Download, Search } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CoaAccount {
  id: string;
  account_code: string;
  account_name: string;
  account_type: string;
}

// CoaResponse is the raw api.get result — might be array or { data: [] }
type CoaResponse = CoaAccount[] | { data: CoaAccount[] };

interface GlLine {
  date: string;
  entryId: string;
  entryNumber: string;
  entryType: string;
  description: string;
  debit: number;
  credit: number;
  runningBalance: number;
}

interface GlData {
  reportType: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  accountType: string;
  period: { from: string; to: string };
  openingBalance: number;
  closingBalance: number;
  totalDebit: number;
  totalCredit: number;
  lineCount: number;
  lines: GlLine[];
}

function fmt(n: number) {
  return new Intl.NumberFormat('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function thisYear() {
  const now = new Date();
  return { from: `${now.getFullYear()}-01-01`, to: now.toISOString().split('T')[0]! };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function GlPage() {
  const [accounts, setAccounts]   = useState<CoaAccount[]>([]);
  const [accountId, setAccountId] = useState('');
  const [from, setFrom]           = useState(thisYear().from);
  const [to, setTo]               = useState(thisYear().to);
  const [glData, setGlData]       = useState<GlData | null>(null);
  const [loading, setLoading]     = useState(false);
  const [coaLoading, setCoaLoading] = useState(true);
  const [search, setSearch]       = useState('');

  // Load chart of accounts on mount
  useEffect(() => {
    api.get<CoaResponse>('/chart-of-accounts?limit=100')
      .then(res => {
        const list = Array.isArray(res) ? res : (res as { data: CoaAccount[] }).data ?? [];
        setAccounts(list as CoaAccount[]);
      })
      .catch(() => setAccounts([]))
      .finally(() => setCoaLoading(false));
  }, []);

  const fetchGl = useCallback(async (acctId: string, f: string, t: string) => {
    if (!acctId) return;
    setLoading(true);
    try {
      const res = await api.get<GlData>(`/finance/reports/gl?accountId=${acctId}&from=${f}&to=${t}`);
      setGlData(res ?? null);
    } catch {
      setGlData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRun = () => fetchGl(accountId, from, to);

  const filteredLines = (glData?.lines ?? []).filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return l.description.toLowerCase().includes(q)
      || l.entryNumber.toLowerCase().includes(q)
      || l.entryType.toLowerCase().includes(q);
  });

  const selectedAccount = accounts.find(a => a.id === accountId);

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <BookOpen className="h-6 w-6 text-indigo-600" />
        <div>
          <h1 className="text-xl font-semibold">General Ledger</h1>
          <p className="text-sm text-muted-foreground">Per-account ledger activity with running balance</p>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3 flex-wrap items-end">
            <div className="flex-1 min-w-48">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Account</label>
              <select
                value={accountId}
                onChange={e => setAccountId(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm"
                disabled={coaLoading}
              >
                <option value="">— Select account —</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.account_code} — {a.account_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">From</label>
              <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                className="border rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">To</label>
              <input type="date" value={to} onChange={e => setTo(e.target.value)}
                className="border rounded px-2 py-1.5 text-sm" />
            </div>
            <Button onClick={handleRun} disabled={!accountId || loading} size="sm">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Run Report
            </Button>
            {glData && (
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" /> Export
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* KPI strip */}
      {glData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Account',          value: `${glData.accountCode} · ${glData.accountName}`, mono: false },
            { label: 'Opening Balance',  value: fmt(glData.openingBalance), mono: true },
            { label: 'Total Dr / Cr',    value: `${fmt(glData.totalDebit)} / ${fmt(glData.totalCredit)}`, mono: true },
            { label: 'Closing Balance',  value: fmt(glData.closingBalance), mono: true },
          ].map(k => (
            <Card key={k.label} className="p-3">
              <div className="text-xs text-muted-foreground mb-1">{k.label}</div>
              <div className={`font-semibold text-sm ${k.mono ? 'font-mono' : ''}`}>{k.value}</div>
            </Card>
          ))}
        </div>
      )}

      {/* Lines table */}
      {glData && (
        <>
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search lines…" value={search} onChange={e => setSearch(e.target.value)}
              className="h-8 w-56 text-sm" />
            <span className="text-xs text-muted-foreground ml-2">{filteredLines.length} lines</span>
          </div>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Ref #</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Opening balance row */}
                    <TableRow className="bg-blue-50/70 text-xs font-medium border-b">
                      <TableCell>{glData.period.from}</TableCell>
                      <TableCell colSpan={3}>Opening Balance</TableCell>
                      <TableCell className="text-right font-mono">—</TableCell>
                      <TableCell className="text-right font-mono">—</TableCell>
                      <TableCell className="text-right font-mono">{fmt(glData.openingBalance)}</TableCell>
                    </TableRow>
                    {filteredLines.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">No transactions in this period.</TableCell></TableRow>
                    )}
                    {filteredLines.map((l, i) => (
                      <TableRow key={`${l.entryId}-${i}`} className="border-b hover:bg-muted/30">
                        <TableCell className="text-xs">{l.date}</TableCell>
                        <TableCell className="font-mono text-xs">{l.entryNumber}</TableCell>
                        <TableCell className="text-xs">
                          <span className="bg-gray-100 text-gray-700 rounded px-1.5 py-0.5 text-xs">{l.entryType}</span>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{l.description}</TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {l.debit > 0 ? fmt(l.debit) : '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {l.credit > 0 ? fmt(l.credit) : '—'}
                        </TableCell>
                        <TableCell className={`px-4 py-2.5 text-right font-mono text-xs font-medium ${l.runningBalance >= 0 ? 'text-blue-700' : 'text-red-600'}`}>
                          {fmt(l.runningBalance)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Closing balance row */}
                    {filteredLines.length > 0 && (
                      <TableRow className="bg-blue-50/70 text-xs font-semibold border-t-2 border-border">
                        <TableCell>{glData.period.to}</TableCell>
                        <TableCell colSpan={3}>Closing Balance</TableCell>
                        <TableCell className="text-right font-mono">{fmt(glData.totalDebit)}</TableCell>
                        <TableCell className="text-right font-mono">{fmt(glData.totalCredit)}</TableCell>
                        <TableCell className={`px-4 py-2 text-right font-mono ${glData.closingBalance >= 0 ? 'text-blue-700' : 'text-red-600'}`}>
                          {fmt(glData.closingBalance)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Empty state before account selection */}
      {!glData && !loading && (
        <div className="text-center py-16 text-muted-foreground">
          <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">Select an account and click Run Report</p>
          <p className="text-sm mt-1">
            {selectedAccount
              ? `Account: ${selectedAccount.account_code} — ${selectedAccount.account_name}`
              : 'Choose from the chart of accounts above'}
          </p>
        </div>
      )}
    </div>
  );
}

